import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BookShowcase } from "@/components/books/BookShowcase";
import { SamplePages } from "@/components/books/SamplePages";
import { BookReviews } from "@/components/books/BookReviews";
import { BookInquiryForm } from "@/components/books/BookInquiryForm";
import { OffSeason } from "@/components/books/OffSeason";
import { api } from "@/lib/api";
import { BOOK_CTA, bookSteps } from "@/lib/books";

const fmtPrice = (p) => (p == null ? "99" : Number(p) % 1 ? Number(p).toFixed(2) : String(Number(p)));

const Hero = () => (
  <section className="relative pt-36 pb-10 lg:pt-44 lg:pb-16 overflow-hidden" style={{ backgroundColor: "#FBF8F1" }} data-testid="books-hero">
    <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="overline mb-5" style={{ color: "var(--gold)" }}>Personalized Swim Books</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] max-w-3xl" style={{ color: "var(--navy)" }} data-testid="books-headline">
          Your Child. Their Story. <span className="italic" style={{ color: "var(--turquoise)" }}>A Swimming Adventure They'll Never Forget.</span>
        </h1>
        <p className="font-body font-light text-base md:text-lg leading-relaxed mt-6 max-w-xl" style={{ color: "var(--muted)" }}>
          One heartwarming swimming story, personalized with your little swimmer as the main character alongside Coach Mimi.
        </p>
        <a href="#book-request" className="btn-primary mt-9 inline-flex" data-testid="books-hero-cta">{BOOK_CTA}</a>
      </Reveal>
    </div>
  </section>
);

const Steps = ({ price }) => (
  <section className="py-24" data-testid="books-how-it-works">
    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="overline mb-4" style={{ color: "var(--gold)" }}>How it works</p>
        <h2 className="font-display text-4xl md:text-5xl mb-14" style={{ color: "var(--navy)" }}>Ordering is <span className="italic">simple.</span></h2>
      </Reveal>
      <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {bookSteps(fmtPrice(price)).map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.08}>
            <li className="h-full p-8 rounded-2xl bg-white border" style={{ borderColor: "rgba(63,184,175,0.3)" }} data-testid={`books-step-${i}`}>
              <span className="font-display text-4xl" style={{ color: "var(--turquoise)" }}>0{i + 1}</span>
              <h3 className="font-display text-2xl mt-4 mb-2" style={{ color: "var(--navy)" }}>{s.title}</h3>
              <p className="font-body font-light text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{s.body}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

const Pricing = ({ price }) => (
  <Reveal>
    <div className="rounded-3xl p-10 text-white h-full" style={{ backgroundColor: "var(--navy)" }} data-testid="books-pricing">
      <p className="overline mb-4" style={{ color: "var(--gold)" }}>Pricing</p>
      <p className="font-display text-6xl mt-1" data-testid="books-price">${fmtPrice(price)}</p>
      <p className="font-body text-sm text-white/80 mt-1">per personalized hardcover, plus shipping and applicable taxes</p>
      <p className="font-body font-light text-sm leading-relaxed text-white/70 mt-8">
        Your total is confirmed before you pay. Payment is made through Zelle using private instructions from Coach Mimi — no banking details are ever collected on this website.
      </p>
      <div className="flex items-start gap-3 mt-8 pt-8 border-t border-white/15">
        <ShieldCheck size={20} className="shrink-0" style={{ color: "var(--turquoise)" }} />
        <p className="font-body text-xs leading-relaxed text-white/70" data-testid="books-privacy-note">
          Your family's privacy comes first. Children's photos are never collected on this form, are only used with your written permission, and no photos or book pages are ever published.
        </p>
      </div>
    </div>
  </Reveal>
);

export default function SwimBooks() {
  const [price, setPrice] = useState(null);
  useEffect(() => { api.get("/books/price").then((r) => setPrice(r.data.price)).catch(() => setPrice(99)); }, []);
  useEffect(() => {
    if (window.location.hash !== "#book-request") return;
    const t = setTimeout(() => document.getElementById("book-request")?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div data-testid="swim-books-page" style={{ "--turquoise": "#2BA8A0" }}>
      <Hero />
      <BookShowcase />
      <SamplePages />
      <BookReviews />
      <OffSeason />
      <Steps price={price} />
      <section id="book-request" className="pb-28 scroll-mt-28">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[2fr_3fr] gap-10 items-start">
          <Pricing price={price} />
          <Reveal delay={0.1}>
            <div className="rounded-3xl p-8 md:p-10 bg-white border h-full" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
              <p className="overline mb-3" style={{ color: "var(--gold)" }}>Request a book</p>
              <h2 className="font-display text-3xl md:text-4xl mb-8" style={{ color: "var(--navy)" }}>Who's the star of the story?</h2>
              <BookInquiryForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
